import { useState } from 'react'
import { ExternalLink, Globe, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

interface WebSearchRendererProps {
  data: unknown
}

interface SearchResult {
  url?: string
  title?: string
  publish_date?: string
  excerpts?: string[]
  // Fallback fields from other APIs
  snippet?: string
  description?: string
  link?: string
}

interface SearchResponse {
  search_id?: string
  results?: SearchResult[]
  // Fallback
  [key: string]: unknown
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '')
  } catch {
    return url
  }
}

/** Clean all excerpts into readable text */
function cleanExcerpts(excerpts: string[]): string {
  if (!excerpts || excerpts.length === 0) return ''
  return excerpts
    .map((e) => {
      // Strip "Section Title: ...\nContent:\n" prefixes
      const cleaned = e
        .replace(/Section Title:.*?\nContent:\n?/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // strip markdown links
        .replace(/\n{3,}/g, '\n\n')
        .trim()
      return cleaned
    })
    .filter(Boolean)
    .join('\n\n')
}

function SearchResultCard({ result }: { result: SearchResult }) {
  const [expanded, setExpanded] = useState(false)
  const url = result.url || result.link || ''
  const title = result.title || getDomain(url) || 'Untitled'

  const fullText = result.excerpts
    ? cleanExcerpts(result.excerpts)
    : result.snippet || result.description || ''

  // Preview: first ~200 chars, cut at word boundary
  const needsExpand = fullText.length > 200
  const preview = needsExpand ? fullText.slice(0, 200).replace(/\s+\S*$/, '') + '…' : fullText

  return (
    <div className="group px-3 py-2.5 hover:bg-muted/30 transition-colors">
      <div className="flex items-start gap-2.5">
        <Globe className="w-3.5 h-3.5 text-muted-foreground mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-1">
          {/* Title + link */}
          <div className="flex items-center gap-1.5">
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline truncate"
              >
                {title}
              </a>
            ) : (
              <span className="text-sm font-medium truncate">{title}</span>
            )}
            {url && (
              <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            )}
          </div>
          {/* Domain + date row */}
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            {url && <span className="font-mono">{getDomain(url)}</span>}
            {result.publish_date && (
              <>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <Calendar className="w-2.5 h-2.5" />
                  {result.publish_date}
                </span>
              </>
            )}
          </div>
          {/* Content with expand */}
          {fullText && (
            <div>
              <p
                className={cn(
                  'text-xs text-muted-foreground leading-relaxed whitespace-pre-line',
                  expanded && 'max-h-[300px] overflow-y-auto',
                )}
              >
                {expanded ? fullText : preview}
              </p>
              {needsExpand && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setExpanded(!expanded)
                  }}
                  className="text-[11px] text-primary hover:underline mt-0.5"
                >
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function WebSearchRenderer({ data }: WebSearchRendererProps) {
  if (!data || typeof data !== 'object') {
    if (typeof data === 'string') {
      // Try parsing as JSON first (MCP returns JSON as text)
      try {
        const parsed = JSON.parse(data)
        return <WebSearchRenderer data={parsed} />
      } catch {
        // Plain text result
        return <pre className="text-xs whitespace-pre-wrap">{data}</pre>
      }
    }
    return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>
  }

  const response = data as SearchResponse

  // Try to find results array
  let results: SearchResult[] = []
  if (Array.isArray(response.results)) {
    results = response.results
  } else if (Array.isArray(data)) {
    results = data as SearchResult[]
  } else {
    for (const val of Object.values(response)) {
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
        results = val
        break
      }
    }
  }

  if (results.length > 0) {
    return (
      <div className="border rounded-lg divide-y divide-border/30 overflow-hidden">
        {results.map((result, i) => (
          <SearchResultCard key={i} result={result} />
        ))}
      </div>
    )
  }

  // Fallback: stringify
  return (
    <pre className="text-xs whitespace-pre-wrap bg-muted/30 p-3 rounded-lg max-h-[300px] overflow-y-auto">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}
